import { FC } from 'react';

import { getInvestorTimeline, type InvestorTimelineState } from '@/api/investing/investor-timeline';
import { Investment } from '@/api/investing/types';
import CheckCircleGreenIcon from '@/assets/svg/ui-elements/check-circle-green.svg';
import { theme, TimeCell, Typography } from '@/shared/design-system';

import {
  CurrentDot,
  IndicatorSlot,
  LabelSlot,
  TimelineList,
  TimelineRow,
  UpcomingRing,
} from './investment-stage-timeline.styled';

interface InvestmentStageTimelineProps {
  investment: Investment;
}

const ICON_SIZE = 16;

const StageStateIcon = ({ state }: { state: InvestorTimelineState }) => {
  if (state === 'done') {
    return <CheckCircleGreenIcon width={ICON_SIZE} height={ICON_SIZE} />;
  }

  return <IndicatorSlot>{state === 'current' ? <CurrentDot /> : <UpcomingRing />}</IndicatorSlot>;
};

/**
 * Vertical investor-facing progress timeline: renders the curated steps from
 * `getInvestorTimeline`, which names the first four after the wizard's own
 * steps — never the raw internal `InvestmentStage` pipeline. Opened from the
 * "View progress" row action in `pending-asset-actions.tsx` — see that file's
 * comment for why (the design-system `Table` has no expandable-row support to
 * host this inline).
 */
export const InvestmentStageTimeline: FC<InvestmentStageTimelineProps> = ({ investment }) => {
  const entries = getInvestorTimeline(investment);

  return (
    <TimelineList>
      {entries.map((entry) => (
        <TimelineRow key={entry.key}>
          <StageStateIcon state={entry.state} />
          <LabelSlot>
            <Typography
              variant="body3"
              fontWeight={entry.state === 'current' ? 600 : 400}
              color={
                entry.state === 'upcoming' ? theme.colors.text.tertiary : theme.colors.text.primary
              }
            >
              {entry.label}
            </Typography>
          </LabelSlot>
          {entry.at ? (
            <TimeCell date={entry.at} direction="column" />
          ) : (
            <Typography variant="body4" color={theme.colors.text.tertiary}>
              —
            </Typography>
          )}
        </TimelineRow>
      ))}
    </TimelineList>
  );
};

export default InvestmentStageTimeline;
